import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, BadgeCheck } from 'lucide-react';
interface Purchase {
  name: string;
  location: string;
  product: string;
  timeAgo: string;
}
const NAMES = [
// Mujeres
'María José', 'Camila', 'Sofía', 'Lucía', 'Valentina', 'Martina', 'Florencia', 'Agustina', 'Micaela', 'Brenda', 'Antonella', 'Paula', 'Romina', 'Daniela', 'Carolina', 'Macarena', 'Julieta', 'Belén', 'Ailín', 'Rocío', 'Ana Clara', 'Mariana', 'Yamila', 'Tamara',
// Hombres
'Martín', 'Lucas', 'Matías', 'Joaquín', 'Federico', 'Nicolás', 'Sebastián', 'Tomás', 'Agustín', 'Franco', 'Gonzalo', 'Maximiliano', 'Bruno', 'Diego', 'Emiliano', 'Ezequiel', 'Iván', 'Cristian', 'Pablo', 'Facundo'];
const LOCATIONS = ['CABA', 'La Plata, Buenos Aires', 'Mar del Plata, Buenos Aires', 'Bahía Blanca, Buenos Aires', 'Quilmes, Buenos Aires', 'Tigre, Buenos Aires', 'San Isidro, Buenos Aires', 'Córdoba Capital', 'Río Cuarto, Córdoba', 'Villa María, Córdoba', 'Rosario, Santa Fe', 'Santa Fe Capital', 'Rafaela, Santa Fe', 'Mendoza Capital', 'San Rafael, Mendoza', 'Godoy Cruz, Mendoza', 'Tucumán', 'San Miguel de Tucumán', 'Salta Capital', 'Jujuy', 'Neuquén Capital', 'Bariloche, Río Negro', 'Cipolletti, Río Negro', 'Posadas, Misiones', 'Resistencia, Chaco', 'Corrientes Capital', 'Paraná, Entre Ríos', 'Concordia, Entre Ríos', 'San Juan Capital', 'San Luis', 'La Rioja', 'Catamarca', 'Santiago del Estero', 'Formosa', 'Comodoro Rivadavia, Chubut', 'Río Gallegos, Santa Cruz', 'Ushuaia, Tierra del Fuego', 'Trelew, Chubut'];
const PRODUCTS = ['Mega Pack Enfermería', 'Mega Pack + Avanzado', 'Mega Pack Enfermería', 'Mega Pack + Avanzado', 'Mega Pack Enfermería'];
const TIME_PHRASES = ['hace unos segundos', 'hace 1 minuto', 'hace 2 minutos', 'hace 4 minutos', 'hace 6 minutos', 'hace 8 minutos', 'hace 11 minutos', 'hace 14 minutos'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function buildPurchase(): Purchase {
  return {
    name: pickRandom(NAMES),
    location: pickRandom(LOCATIONS),
    product: pickRandom(PRODUCTS),
    timeAgo: pickRandom(TIME_PHRASES)
  };
}
export function PurchaseNotifications() {
  const [current, setCurrent] = useState<Purchase | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let firstTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    const cycle = () => {
      setCurrent(buildPurchase());
      setVisible(true);
      // Visible for ~5s, then hide
      hideTimer = setTimeout(() => {
        setVisible(false);
        // After hiding, wait at least 10s (10–13s random) before next popup
        const gap = 10000 + Math.random() * 3000;
        nextTimer = setTimeout(cycle, gap);
      }, 5000);
    };
    // First popup after 8s
    firstTimer = setTimeout(cycle, 8000);
    return () => {
      clearTimeout(firstTimer);
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, []);
  const initial = current?.name?.charAt(0).toUpperCase() ?? '';
  return <div className="fixed bottom-3 left-3 sm:bottom-5 sm:left-5 z-40 pointer-events-none" data-id="element-423">
      <AnimatePresence data-id="element-424">
        {visible && current && <motion.div key={current.name + current.timeAgo} initial={{
        opacity: 0,
        x: -30,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -30,
        y: 10
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto max-w-[290px] sm:max-w-[340px] bg-white border border-clinical-line shadow-[0_12px_32px_rgba(11,42,74,0.18)] rounded-2xl pl-3 pr-2.5 py-2.5 flex items-center gap-3" role="status" aria-live="polite" data-id="element-425">
            <div className="relative flex-shrink-0" data-id="element-426">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-clinical-soft border border-clinical-line flex items-center justify-center" data-id="element-427">
                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-clinical-mid" strokeWidth={2.2} data-id="element-428" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full" data-id="element-429">
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-500 fill-white" strokeWidth={2.5} data-id="element-430" />
              </div>
            </div>

            <div className="flex-1 min-w-0 pr-1" data-id="element-431">
              <p className="text-[12px] sm:text-[13px] text-clinical-deep leading-tight" data-id="element-432">
                <span className="font-bold" data-id="element-433">{current.name}</span>
                <span className="text-clinical-ink/70" data-id="element-434"> de </span>
                <span className="font-semibold" data-id="element-435">{current.location}</span>
              </p>
              <p className="text-[11px] sm:text-xs text-clinical-ink/80 leading-tight mt-0.5" data-id="element-436">
                compró el{' '}
                <span className="font-semibold text-clinical-mid" data-id="element-437">
                  {current.product}
                </span>
              </p>
              <p className="text-[10px] text-clinical-ink/50 mt-0.5" data-id="element-438">
                {current.timeAgo} · compra verificada
              </p>
            </div>

            <button onClick={() => setVisible(false)} aria-label="Cerrar notificación" className="text-clinical-ink/30 hover:text-clinical-ink/70 transition-colors flex-shrink-0 p-1" data-id="element-439">
              <X className="w-3.5 h-3.5" data-id="element-440" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}